export type RecommendationDismissType = 'campaign' | 'keyword' | 'search_term' | 'rule';

const STORAGE_PREFIX = 'recommendation-dismissals:';

function storageKey(profileId: string): string {
  return `${STORAGE_PREFIX}${profileId}`;
}

function readDismissed(profileId: string): Set<string> {
  try {
    const raw = localStorage.getItem(storageKey(profileId));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((item): item is string => typeof item === 'string'));
  } catch {
    return new Set();
  }
}

function writeDismissed(profileId: string, dismissed: Set<string>): void {
  try {
    localStorage.setItem(storageKey(profileId), JSON.stringify([...dismissed]));
  } catch {
    // localStorage may be unavailable
  }
}

export function campaignRecommendationFingerprint(
  adProduct: string,
  campaignId: string,
  variant: 'high' | 'low'
): string {
  return `campaign:${adProduct}:${campaignId}:${variant}`;
}

export function keywordRecommendationFingerprint(keywordId: string): string {
  return `keyword:${keywordId}`;
}

export function searchTermRecommendationFingerprint(
  campaignId: string,
  adGroupId: string,
  searchTerm: string
): string {
  return `search_term:${campaignId}:${adGroupId}:${searchTerm}`;
}

export function ruleRecommendationFingerprint(tableId: string, entityId: string): string {
  return `rule:${tableId}:${entityId}`;
}

export function loadDismissedRecommendations(profileId: string): Set<string> {
  return readDismissed(profileId);
}

export function isRecommendationDismissed(profileId: string, fingerprint: string): boolean {
  return readDismissed(profileId).has(fingerprint);
}

export function dismissRecommendations(profileId: string, fingerprints: string[]): Set<string> {
  const next = readDismissed(profileId);
  for (const fingerprint of fingerprints) next.add(fingerprint);
  writeDismissed(profileId, next);
  return next;
}

export function undoDismissRecommendations(profileId: string, fingerprints: string[]): Set<string> {
  const next = readDismissed(profileId);
  for (const fingerprint of fingerprints) next.delete(fingerprint);
  writeDismissed(profileId, next);
  return next;
}

export function filterDismissedRows<T>(
  rows: T[],
  dismissed: Set<string>,
  fingerprintFor: (row: T) => string
): T[] {
  if (!dismissed.size) return rows;
  return rows.filter((row) => !dismissed.has(fingerprintFor(row)));
}
